/**
 * Centralized API environment configuration.
 *
 * Stores the active API + WS base URLs in local storage so they survive
 * app restarts. Every API module imports getApiBase() / getWsUrl() from here
 * instead of reading process.env directly.
 */

import * as SecureStore from "expo-secure-store";
import { Platform } from "react-native";

import { isProductionBuild, presetForApiUrl } from "@/lib/build-channel";

// ── Storage helper (same pattern as auth.tsx) ──

const storage = {
    get: (key: string) =>
        Platform.OS === "web"
            ? Promise.resolve(localStorage.getItem(key))
            : SecureStore.getItemAsync(key),
    set: (key: string, value: string) =>
        Platform.OS === "web"
            ? Promise.resolve(localStorage.setItem(key, value) as undefined)
            : SecureStore.setItemAsync(key, value),
    remove: (key: string) =>
        Platform.OS === "web"
            ? Promise.resolve(localStorage.removeItem(key) as undefined)
            : SecureStore.deleteItemAsync(key)
};

const STORAGE_KEY = "api-env-config-v2";

// ── Types ──

export interface ApiEnvironment {
    name: string;
    apiUrl: string; // e.g. "https://api2.taskshealth.com" (no /api suffix)
    wsUrl: string; // e.g. "wss://websockets.taskshealth.com"
}

export type PresetKey = "production" | "staging" | "local";

// ── Platform-aware local URL ──
// Honor EXPO_PUBLIC_API_URL when set (baked at build time) so physical devices
// reach the Mac's LAN IP instead of "localhost" (which would point at the device itself).

function buildLocalApiUrl(): string {
    const envUrl = process.env.EXPO_PUBLIC_API_URL;
    if (envUrl && !envUrl.includes("taskshealth.com")) {
        return envUrl;
    }
    return Platform.OS === "android" ? "http://10.0.2.2:4000" : "http://localhost:4000";
}

const LOCAL_API_URL = buildLocalApiUrl();
const LOCAL_WS_URL = LOCAL_API_URL.replace(/^http/, "ws");

// ── Presets ──

export const PRESETS: Record<PresetKey, ApiEnvironment> = {
    production: {
        name: "Production",
        apiUrl: "https://api2.taskshealth.com",
        wsUrl: "wss://websockets.taskshealth.com"
    },
    staging: {
        name: "Staging",
        apiUrl: "https://api-staging.taskshealth.com",
        wsUrl: "wss://websockets-staging.taskshealth.com"
    },
    local: {
        name: `Local (${LOCAL_API_URL})`,
        apiUrl: LOCAL_API_URL,
        wsUrl: LOCAL_WS_URL
    }
};

// ── Determine build-time default ──
// In production builds EXPO_PUBLIC_API_URL points at prod;
// in dev builds it's usually unset → fall back to local.

function detectDefaultPreset(): PresetKey {
    return presetForApiUrl(process.env.EXPO_PUBLIC_API_URL);
}

// ── In-memory active environment ──
// Initialised to the build-time default; overwritten from storage on boot.

let _active: ApiEnvironment = PRESETS[detectDefaultPreset()];
let _loaded = false;
let _loadPromise: Promise<void> | null = null;

/**
 * Load the persisted environment from storage (called once at app start).
 * Safe to call multiple times — subsequent calls are no-ops.
 */
export function loadApiEnv(): Promise<void> {
    if (_loaded) {
        return Promise.resolve();
    }
    if (_loadPromise) {
        return _loadPromise;
    }
    // A baked-in dev API URL (e.g. a LAN IP or ngrok tunnel for on-device
    // testing) is authoritative: it should win over — and clear — any stale
    // environment a previous install persisted on this device. Production
    // builds bake a *.taskshealth.com URL and are unaffected (they fall
    // through to the normal stored-override behavior below).
    const bakedDevUrl = process.env.EXPO_PUBLIC_API_URL;
    if (bakedDevUrl && !bakedDevUrl.includes("taskshealth.com")) {
        _active = PRESETS.local;
        _loaded = true;
        _loadPromise = storage.remove(STORAGE_KEY).catch(() => {});
        return _loadPromise;
    }
    // The production app has no environment switcher (lib/build-channel.ts), so
    // it must not honour one saved earlier either: a phone switched to staging
    // or a custom URL before this release would otherwise stay there with no
    // way back. It always talks to the API it was built for.
    if (isProductionBuild()) {
        _active = PRESETS[detectDefaultPreset()];
        _loaded = true;
        _loadPromise = Promise.resolve();
        return _loadPromise;
    }
    _loadPromise = storage
        .get(STORAGE_KEY)
        .then((raw) => {
            if (raw) {
                try {
                    const parsed = JSON.parse(raw) as ApiEnvironment;
                    if (parsed.apiUrl && parsed.wsUrl) {
                        _active = parsed;
                    }
                } catch {
                    // corrupt data — keep default
                }
            }
            _loaded = true;
        })
        .catch(() => {
            _loaded = true;
        });
    return _loadPromise;
}

/** Get the currently active environment object. */
export function getActiveEnv(): ApiEnvironment {
    return _active;
}

/** API base with /api suffix, ready for endpoint calls. */
export function getApiBase(): string {
    return `${_active.apiUrl}/api`;
}

/** WebSocket URL with /api/ws path. */
export function getWsUrl(): string {
    // If wsUrl already contains a path (e.g. dedicated ws domain), use as-is
    if (_active.wsUrl.includes("/api/ws")) {
        return _active.wsUrl;
    }
    // For dedicated WS domains (production/staging), no path needed
    const url = new URL(_active.wsUrl);
    if (url.pathname === "/" || url.pathname === "") {
        // Dedicated WS domain like wss://websockets.taskshealth.com → use as-is
        // Local like ws://localhost:4000 → append /api/ws
        if (url.port) {
            return `${_active.wsUrl}/api/ws`;
        }
        return _active.wsUrl;
    }
    return _active.wsUrl;
}

/**
 * Switch to a new environment. Persists to storage and updates in-memory.
 * The app should be restarted (or at minimum re-mount API-consuming screens)
 * for the change to fully take effect.
 */
export async function setApiEnv(env: ApiEnvironment): Promise<void> {
    _active = env;
    await storage.set(STORAGE_KEY, JSON.stringify(env));
}

/** Clear override — revert to build-time default on next app launch. */
export async function clearApiEnv(): Promise<void> {
    _active = PRESETS[detectDefaultPreset()];
    await storage.remove(STORAGE_KEY);
}

/** Check which preset (if any) matches the active env. */
export function activePresetKey(): PresetKey | "custom" {
    for (const [key, preset] of Object.entries(PRESETS)) {
        if (preset.apiUrl === _active.apiUrl && preset.wsUrl === _active.wsUrl) {
            return key as PresetKey;
        }
    }
    return "custom";
}
